import type { LayerLabelConfiguration } from '../labels/model';
import type { AnnotationFontFamilyId, AnnotationFontWeight } from '../annotations/typography';
import { AUTHORED_FONT_CSS } from '../typography/fonts';

export const MAPLIBRE_FONT_FAMILY = AUTHORED_FONT_CSS;
type RenderTypography = Omit<
  Pick<LayerLabelConfiguration, 'fontFamily' | 'fontStyle' | 'fontWeight'>,
  'fontFamily' | 'fontWeight'
> & {
  readonly fontFamily: AnnotationFontFamilyId;
  readonly fontWeight: LayerLabelConfiguration['fontWeight'] | AnnotationFontWeight;
};

/**
 * The first renderer-only alias lets MapLibre infer weight/style; the second
 * selects the bundled variable family through normal CSS fallback.
 */
export function mapLibreLabelFontStack(labels: RenderTypography): readonly string[] {
  const family = MAPLIBRE_FONT_FAMILY[labels.fontFamily];
  const weight =
    labels.fontWeight === 'bold' || labels.fontWeight === 700
      ? 'Bold'
      : labels.fontWeight === 600
        ? 'Semibold'
        : labels.fontWeight === 500
          ? 'Medium'
          : 'Regular';
  const style = labels.fontStyle === 'italic' ? ' Italic' : '';
  return [`PlaceioMaps-${weight}${style.replace(' ', '-')}`, family, 'Arial', 'Helvetica'];
}

export function mapLibreLabelFontLoadRequest(labels: RenderTypography): string {
  const family = MAPLIBRE_FONT_FAMILY[labels.fontFamily];
  const weight =
    typeof labels.fontWeight === 'number'
      ? labels.fontWeight
      : labels.fontWeight === 'bold'
        ? 700
        : 400;
  const style = labels.fontStyle === 'italic' ? 'italic' : 'normal';
  return `${style} ${weight} 48px "${family}"`;
}

/** Returns null outside a browser; failed faces fall back without breaking rendering. */
export function loadMapLibreLabelFonts(
  labels: readonly RenderTypography[],
  strict = false,
): Promise<void> | null {
  if (typeof document === 'undefined' || !document.fonts?.load) return null;
  const requests = [...new Set(labels.map(mapLibreLabelFontLoadRequest))];
  if (strict)
    return Promise.all(
      requests.map(async (request) => {
        const faces = await document.fonts.load(request, 'A\u0100');
        if (!faces.length) throw new Error('The authored local font could not be loaded.');
      }),
    ).then(() => undefined);
  return Promise.allSettled(requests.map((request) => document.fonts.load(request))).then(
    () => undefined,
  );
}
